import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building, MapPin, Phone, Mail, Globe, FileText, Briefcase, Calendar, Landmark, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useLogo } from '../context/LogoContext';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompanyPage() {
  const { t, language } = useLanguage();
  const { logoUrl } = useLogo();
  const [company, setCompany] = useState(null);
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/public/company?lang=${language}`).then(r => r.data).catch(() => null),
      axios.get(`${API_URL}/api/public/about?lang=${language}`).then(r => r.data).catch(() => null),
    ]).then(([c, a]) => {
      setCompany(c);
      setAbout(a);
    }).finally(() => setLoading(false));
  }, [language]);

  const infoItems = [
    { key: 'company_name', icon: Building, value: company?.company_name },
    { key: 'representative', icon: Briefcase, value: company?.representative },
    { key: 'established', icon: Calendar, value: company?.established },
    { key: 'capital', icon: Landmark, value: company?.capital },
    { key: 'license_number', icon: Award, value: company?.license_number },
    { key: 'address', icon: MapPin, value: company?.address },
    { key: 'phone', icon: Phone, value: company?.phone },
    { key: 'email', icon: Mail, value: company?.email },
    { key: 'website', icon: Globe, value: company?.website },
  ];

  const hasData = company && Object.values(company).some(v => v);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <img src={logoUrl} alt="PRC Remit" className="h-9 w-9 rounded-full object-cover" />
              <span className="font-logo text-2xl font-bold text-primary">PRC Remit</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <Link to="/">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  {t('company_back')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-20" data-testid="company-page">
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <Building className="h-8 w-8 text-accent" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3">{t('company_page_title')}</h1>
          <p className="text-muted-foreground text-lg">{t('company_page_subtitle')}</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : !hasData && !about?.content ? (
          <div className="text-center py-16 bg-card border border-border/50 rounded-2xl">
            <Building className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-30" />
            <p className="text-muted-foreground">{t('company_no_data')}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Company Info Grid */}
            {hasData && (
              <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
                <div className="divide-y divide-border">
                  {infoItems.filter(item => item.value).map(item => (
                    <div key={item.key} className="flex items-start gap-4 px-6 py-5 hover:bg-secondary/20 smooth-transition" data-testid={`company-${item.key}`}>
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <item.icon className="h-5 w-5 text-accent" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground mb-0.5">{t(`company_${item.key}`)}</div>
                        <div className="text-primary font-medium">
                          {item.key === 'email' ? (
                            <a href={`mailto:${item.value}`} className="text-accent hover:underline">{item.value}</a>
                          ) : item.key === 'website' ? (
                            <a href={item.value} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{item.value}</a>
                          ) : (
                            item.value
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Business Description */}
            {company?.business_description && (
              <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="company-description">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="h-5 w-5 text-accent" />
                  <h2 className="font-heading text-xl font-semibold text-primary">{t('company_business_description')}</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {company.business_description}
                </p>
              </div>
            )}

            {/* About Content (Markdown) */}
            {about?.content && (
              <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="company-about">
                <div className="prose prose-slate max-w-none
                  prose-headings:font-heading prose-headings:text-primary
                  prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-muted-foreground prose-p:leading-relaxed
                  prose-li:text-muted-foreground
                  prose-strong:text-primary
                  prose-table:border-collapse
                  prose-th:bg-secondary/50 prose-th:border prose-th:border-border prose-th:px-4 prose-th:py-2 prose-th:text-left
                  prose-td:border prose-td:border-border prose-td:px-4 prose-td:py-2
                ">
                  <ReactMarkdown>{about.content}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-border bg-secondary/30 py-8 mt-12">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center text-sm text-muted-foreground">
          <p>&copy; 2026 PRC Remit. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
